/* ET - Countdown: live countdown to a wall-clock date/time in an IANA timezone. */
(() => {
  if (customElements.get('et-countdown')) return;

  const pad = (n) => String(n).padStart(2, '0');

  // Offset (ms) between the wall-clock time in `timeZone` and UTC at instant `ts`.
  const zoneOffset = (ts, timeZone) => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(new Date(ts));
    const p = {};
    parts.forEach(({ type, value }) => { p[type] = Number(value); });
    return Date.UTC(p.year, p.month - 1, p.day, p.hour % 24, p.minute, p.second) - ts;
  };

  // Converts "YYYY-MM-DD" + "HH:MM" in `timeZone` to a UTC timestamp (DST-safe).
  const toTimestamp = (date, time, timeZone) => {
    const [y, m, d] = String(date).split('-').map(Number);
    const [h = 0, min = 0] = String(time || '00:00').split(':').map(Number);
    if (!y || !m || !d || Number.isNaN(h) || Number.isNaN(min)) return NaN;

    const wall = Date.UTC(y, m - 1, d, h, min);
    try {
      const first = zoneOffset(wall, timeZone);
      let ts = wall - first;
      const second = zoneOffset(ts, timeZone);
      if (second !== first) ts = wall - second;
      return ts;
    } catch (e) {
      // Unknown timezone: fall back to the visitor's local time.
      return new Date(y, m - 1, d, h, min).getTime();
    }
  };

  class EtCountdown extends HTMLElement {
    connectedCallback() {
      this.target = toTimestamp(this.dataset.date, this.dataset.time, this.dataset.timezone);
      if (Number.isNaN(this.target)) {
        this.hidden = true;
        return;
      }

      this.values = {};
      this.querySelectorAll('[data-et-countdown-value]').forEach((el) => {
        this.values[el.dataset.etCountdownValue] = el;
      });

      this.tick();
      this.classList.add('is-ready');
      if (!this.expired) this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    set(unit, value) {
      const el = this.values[unit];
      if (el) el.textContent = pad(value);
    }

    tick() {
      const diff = this.target - Date.now();
      if (diff <= 0) {
        this.expire();
        return;
      }

      const total = Math.floor(diff / 1000);
      this.set('days', Math.floor(total / 86400));
      this.set('hours', Math.floor((total % 86400) / 3600));
      this.set('minutes', Math.floor((total % 3600) / 60));
      this.set('seconds', total % 60);
    }

    expire() {
      if (this.expired) return;
      this.expired = true;
      window.clearInterval(this.timer);
      ['days', 'hours', 'minutes', 'seconds'].forEach((unit) => this.set(unit, 0));

      const action = this.dataset.expired;
      if (action === 'hide') {
        this.hidden = true;
      } else if (action === 'text') {
        const units = this.querySelector('.et-countdown__units');
        const message = this.querySelector('[data-et-countdown-expired]');
        if (units) units.hidden = true;
        if (message) message.hidden = false;
      }

      this.classList.add('is-expired');
      this.dispatchEvent(new CustomEvent('et-countdown:expired', { bubbles: true }));
    }
  }

  customElements.define('et-countdown', EtCountdown);
})();
